// src/hooks/useClaims.js
import { useState, useEffect, useCallback } from 'react';
import { auth } from '../config/firebase';
import { onIdTokenChanged } from 'firebase/auth';
import claimsService from '../services/claimsService';

export const useClaims = (bookId = null) => {
  const [claims, setClaims] = useState(null);
  const [role, setRole] = useState('user');
  const [bookRole, setBookRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    canManage: false,
    canEdit: false,
    canContribute: false,
    canView: true,
    isAdmin: false,
    isSuperAdmin: false
  });

  const refreshClaims = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setClaims(null);
        setRole('user');
        setBookRole(null);
        setPermissions({
          canManage: false,
          canEdit: false,
          canContribute: false,
          canView: true,
          isAdmin: false,
          isSuperAdmin: false
        });
        return;
      }

      const tokenResult = await user.getIdTokenResult();
      const userClaims = tokenResult.claims;
      
      setClaims(userClaims);
      setRole(userClaims.role || 'user');
      
      const userBookRole = bookId ? userClaims.bookRoles?.[bookId] : null;
      setBookRole(userBookRole);
      
      // Calculate permissions
      const isSuperAdmin = userClaims.role === 'super-admin';
      const isAdmin = userClaims.role === 'admin' || isSuperAdmin;
      
      const canManage = isSuperAdmin || ['owner', 'admin'].includes(userBookRole);
      const canEdit = canManage || userBookRole === 'editor';
      const canContribute = canEdit || userBookRole === 'contributor';
      
      setPermissions({
        canManage,
        canEdit,
        canContribute,
        canView: true,
        isAdmin,
        isSuperAdmin
      });
    } catch (error) {
      console.error('Error refreshing claims:', error);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user) {
        refreshClaims();
      } else {
        setLoading(false);
        setClaims(null);
        setRole('user');
        setBookRole(null);
      }
    });

    return () => unsubscribe();
  }, [refreshClaims]);

  return {
    claims,
    role,
    bookRole,
    loading,
    permissions,
    refreshClaims,
    // Convenience methods
    isOwner: bookRole === 'owner',
    isBookAdmin: ['owner', 'admin'].includes(bookRole),
    hasAccess: !!bookRole || permissions.isSuperAdmin
  };
};

export default useClaims;